import React from "react";
import { useHistory } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import {
  BLPRecentActivityDto,
  BLPRecentActivityRecordDto,
  useBLPRecentActivities,
} from "../../api/getBLPRecentActivities";

export interface RecentActivityProps {
  onViewAll?: () => void;
  onRecordClick?: (recordIds: string[]) => void;
}

export const mapRawToActivity = (item: BLPRecentActivityDto) => {
  const action = String(item.action ?? "")
    .trim()
    .toLowerCase();

  const records = Array.isArray(item.records) ? item.records : [];

  const recordIds = records
    .map((record) => String(record.recordId ?? "").trim())
    .filter((recordId) => recordId !== "");

  return {
    id: item.id,

    createdDate: item.createdOn ?? "",

    displayCreatedBy: item.displayCreatedBy ?? item.createdBy ?? "",

    action,
    bucket: "Succeeded" as const,

    recordId:
      item.recordId !== null && item.recordId !== undefined
        ? String(item.recordId)
        : "",

    recordIds,

    recordCount: Number(item.recordCount ?? records.length),

    records,
  };
};

export type BLPRecentActivityItem = ReturnType<typeof mapRawToActivity>;

const renderRecord = (record: BLPRecentActivityRecordDto): React.ReactNode => {
  const recordName = String(record.recordName ?? "").trim();

  const typeName = String(record.busLicensePermitTypeName ?? "").trim();

  const article = String(record.article ?? "").trim();

  const officeName = String(record.officeName ?? "").trim();

  const hasRecordName = recordName !== "";

  const hasType = typeName !== "";

  const hasOffice = officeName !== "";

  return (
    <>
      {hasRecordName && <strong className="font-bold">{recordName}</strong>}

      {hasType && (
        <>
          {hasRecordName ? ", " : ""}

          {article ? `${article} ` : ""}

          {typeName}
        </>
      )}

      {hasOffice && (
        <>
          {hasRecordName || hasType ? ", for " : "for "}

          {officeName}
        </>
      )}
    </>
  );
};

const getActionSuffix = (action: string, isMultiple: boolean): string => {
  if (action !== "added") {
    return ".";
  }

  if (isMultiple) {
    return " as new records.";
  }

  return " as a new record.";
};

export const renderActivityMessage = (
  item: BLPRecentActivityItem,
): React.ReactNode => {
  const hasRecords = item.records.length > 0;

  const isMultiple = item.recordCount > 1;

  const action = item.action || "performed an action";

  if (!hasRecords) {
    return (
      <>
        <strong className="font-bold">{item.displayCreatedBy}</strong> {action}.
      </>
    );
  }

  const actionSuffix = getActionSuffix(action, isMultiple);

  return (
    <>
      <strong className="font-bold">{item.displayCreatedBy}</strong> {action}{" "}
      {isMultiple && <>{item.recordCount} records: </>}
      {item.records.map((record, index) => (
        <React.Fragment key={`${record.recordId}-${index}`}>
          {index > 0 ? "; " : ""}

          {renderRecord(record)}
        </React.Fragment>
      ))}
      {actionSuffix}
    </>
  );
};

const ActivityRow = ({
  item,
  isLast,
  onClick,
}: {
  item: BLPRecentActivityItem;
  isLast: boolean;
  onClick?: () => void;
}) => {
  const dt = new Date(String(item.createdDate).replace(" ", "T"));

  const dateText = Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`
        flex
        w-full
        items-start
        gap-1.5
        border-0
        bg-transparent
        px-1
        py-1
        text-left
        ${isLast ? "" : "border-b border-gray-100"}
        ${onClick ? "cursor-pointer hover:bg-gray-50" : ""}
      `}
    >
      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />

      <p className="m-0 min-w-0 flex-1 font-sans text-[11px] leading-[15px] text-gray-700 2xl:text-xs 2xl:leading-[17px]">
        {renderActivityMessage(item)}
      </p>

      <span className="mt-0.5 shrink-0 whitespace-nowrap font-sans text-[9px] text-gray-400 2xl:text-[10px]">
        {dateText}
      </span>
    </button>
  );
};

const RecentActivity: React.FC<RecentActivityProps> = ({
  onViewAll,
  onRecordClick,
}) => {
  const history = useHistory();

  const { data: rawData, isLoading, error } = useBLPRecentActivities(7);

  const data = (rawData ?? []).map(mapRawToActivity);

  const previewData = data.slice(0, 6);

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
      return;
    }

    history.push("/blp/recent-activities");
  };

  const emptyState = (label: string) => (
    <div className="flex flex-1 items-center justify-center font-sans text-xs text-gray-400">
      {label}
    </div>
  );

  let activityContent: React.ReactNode;

  if (isLoading) {
    activityContent = emptyState("Loading…");
  } else if (error) {
    activityContent = emptyState("Failed to load activity");
  } else if (data.length === 0) {
    activityContent = emptyState("No activity");
  } else {
    activityContent = (
      <div className="flex flex-col overflow-hidden">
        {previewData.map((item, index) => (
          <ActivityRow
            key={item.id}
            item={item}
            isLast={index === previewData.length - 1}
            onClick={
              item.recordIds.length > 0 && onRecordClick
                ? () => onRecordClick(item.recordIds)
                : undefined
            }
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="m-0 text-left font-sans text-sm font-bold text-primary 2xl:text-base">
            Recent Activity
          </h2>

          <button
            type="button"
            onClick={handleViewAll}
            className="cursor-pointer border-0 bg-transparent p-0 font-sans text-xs font-medium text-primary underline 2xl:text-sm"
          >
            View all
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-3 py-2">
        {activityContent}
      </div>
    </div>
  );
};

export default RecentActivity;
